import { Select } from 'antd';
import { PageHeader, TWDataTable } from '../../components/ui';
import { headerCls, DataTableSkeleton } from '../../data/columns';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import terminalApi from '../../services/terminalApi';
import { Tooltip } from 'react-tooltip';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronUpDownIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/20/solid';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  AdjustmentsHorizontalIcon,
  ComputerDesktopIcon,
  BuildingStorefrontIcon,
  WifiIcon,
  BoltIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  BellSlashIcon,
  DeviceTabletIcon,
} from '@heroicons/react/24/outline';
import { useTransition, animated, useSpringRef } from '@react-spring/web';
import KioskLinkUpsertModal from './components/kiosk-link-upsert-modal';
import { openDevicePath } from './kiosks-utils';
import NoData2 from '../../assets/no-data-2.svg';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import dayjs from 'dayjs';
import { setStartAndEndDate } from '../../utils/setStartAndEndDate';
import { openCloseStatus } from '../../data/enums';
import getAvailability from '../../utils/getAvailability';
import './kiosks.css';

const utc = require('dayjs/plugin/utc');
const isBetween = require('dayjs/plugin/isBetween');
const timezone = require('dayjs/plugin/timezone');
const isoWeek = require('dayjs/plugin/isoWeek');

dayjs.extend(utc);
dayjs.extend(isBetween);
dayjs.extend(customParseFormat);
dayjs.extend(timezone);
dayjs.extend(isoWeek);
dayjs.Ls.en.weekStart = 1;

const cn = (...c) => c.filter(Boolean).join(' ');

const AVATAR_COLORS = [
  'from-blue-500 to-sky-500',
  'from-violet-500 to-purple-500',
  'from-emerald-500 to-teal-500',
  'from-rose-500 to-pink-500',
  'from-amber-500 to-orange-500',
  'from-cyan-500 to-blue-500',
];

const getAvatarColor = (name) => {
  const s = String(name || '');
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = s.charCodeAt(i) + hash * 31;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const getInitials = (name) => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || '').join('') || '?';
};

const availabilityBadge = (status, label) => {
  const tone =
    status === 'open'
      ? 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-400/20'
      : status === 'opensSoon'
        ? 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-400/20'
        : 'bg-slate-100 text-slate-600 ring-slate-200 dark:bg-slate-700/50 dark:text-slate-300 dark:ring-slate-600/40';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1',
        tone,
      )}
    >
      <span
        className={cn(
          'h-1.5 w-1.5 rounded-full',
          status === 'open'
            ? 'bg-emerald-500'
            : status === 'opensSoon'
              ? 'bg-amber-500'
              : 'bg-slate-400',
        )}
      />
      {label}
    </span>
  );
};

const SortableHeader = ({ label, columnKey, sortConfig, onSort, align }) => {
  const isActive = sortConfig.key === columnKey;
  const justify =
    align === 'center'
      ? 'justify-center'
      : align === 'right'
        ? 'justify-end'
        : 'justify-start';
  return (
    <button
      type="button"
      onClick={() => onSort && onSort(columnKey)}
      className={`inline-flex w-full select-none items-center gap-1 ${justify} ${headerCls} cursor-pointer`}
    >
      {label}
      {isActive ? (
        sortConfig.orderby === 1 ? (
          <ChevronUpIcon className="h-3 w-3" />
        ) : (
          <ChevronDownIcon className="h-3 w-3" />
        )
      ) : (
        <ChevronUpDownIcon className="h-3 w-3 opacity-40" />
      )}
    </button>
  );
};

const Kiosks = () => {
  const roles = useSelector((state) => state.authReducer.roles);
  const [search, setSearch] = useState({
    name: '',
    haserrors: true,
    isconnected: '',
    openclosestatus: '',
  });
  const [kioskList, setKioskList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [selectedKiosk, setSelectedKiosk] = useState(null);
  const [openKioskId, setOpenKioskId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [dialogs, setDialogs] = useState({
    showKioskLinkUpsertModal: false,
  });
  const [loading, setLoading] = useState({
    listLoading: false,
  });

  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 20,
    totalRecords: null,
  });

  const [sortConfig, setSortConfig] = useState({
    key: null,
    orderby: 0,
  });

  const sortByAvailability = (list) =>
    [...list].sort((a, b) => {
      const statusA = getAvailability(a.store);
      const statusB = getAvailability(b.store);
      return (
        getStatusPriority(statusA.status) - getStatusPriority(statusB.status)
      );
    });

  const getValueByPath = (obj, path) =>
    path.split('.').reduce((acc, part) => (acc ? acc[part] : undefined), obj);

  const sortByKey = (list, key, orderby) =>
    [...list].sort((a, b) => {
      const av = getValueByPath(a, key);
      const bv = getValueByPath(b, key);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      let cmp;
      if (typeof av === 'number' && typeof bv === 'number') {
        cmp = av - bv;
      } else if (key === 'lastup' || key === 'lastconnectionupdate') {
        cmp = dayjs(av).valueOf() - dayjs(bv).valueOf();
      } else if (typeof av === 'boolean' || typeof bv === 'boolean') {
        cmp = (av ? 1 : 0) - (bv ? 1 : 0);
      } else {
        cmp = String(av).localeCompare(String(bv));
      }
      return orderby === 1 ? cmp : -cmp;
    });

  const applySort = (list, cfg) => {
    if (!cfg.key) return sortByAvailability(list);
    return sortByKey(list, cfg.key, cfg.orderby);
  };

  const getKioksList = async (searchQuery = '', cfg = sortConfig) => {
    setLoading((prev) => ({ ...prev, listLoading: true }));

    const startdate = setStartAndEndDate(dayjs(), 'startdate', '0');
    const enddate = setStartAndEndDate(dayjs(), 'enddate', '0');

    try {
      const query = `?addclickcount=true&startdate=${startdate}&enddate=${enddate}&pageNumber=1&pageSize=100&isdeleted=false&${searchQuery}&${Object.keys(
        search,
      )
        .map((key) => `${key}=${search[key]}`)
        .join('&')}`;

      const list = await terminalApi.list(query);
      if (list.data.data.length > 0) {
        setKioskList(list.data.data);
        setFilteredList(
          applySort(list.data.data, cfg).slice(0, pagination.pageSize),
        );
        setPagination({
          ...pagination,
          totalRecords: Math.ceil(list.data.data.length),
        });
      } else {
        setKioskList([]);
        setFilteredList([]);
        setPagination({
          ...pagination,
          totalRecords: 0,
        });
      }
    } catch (error) {
    } finally {
      setLoading((prev) => ({ ...prev, listLoading: false }));
    }
  };

  const requestSort = (key) => {
    const nextOrderby =
      sortConfig.key === key && sortConfig.orderby === 1 ? 0 : 1;
    const nextCfg = { key, orderby: nextOrderby };
    setSortConfig(nextCfg);
    setPagination((prev) => ({ ...prev, pageNumber: 1 }));
    if (kioskList.length > 0) {
      setFilteredList(
        applySort(kioskList, nextCfg).slice(0, pagination.pageSize),
      );
    }
  };

  const onClickEditKioskLink = (kiosk) => {
    setSelectedKiosk(kiosk);
    setDialogs((prev) => ({
      ...prev,
      showKioskLinkUpsertModal: true,
    }));
  };

  const onClickOpenReaderLinkNewTab = (link) => {
    window.open(link, '_blank');
  };

  const onSubmitReaderLink = async (link) => {
    try {
      const body = {
        ...selectedKiosk,
        link,
      };
      await terminalApi.upsert(body);
    } catch (e) {
    } finally {
      getKioksList();
      setDialogs((prev) => ({
        ...prev,
        showKioskLinkUpsertModal: false,
      }));
    }
  };

  const checkConnection = (date) => dayjs().diff(dayjs(date), 'minutes') <= 7;

  const getStatusPriority = (status) => {
    if (status === 'opensSoon') return 1;
    if (status === 'open') return 2;
    if (status === 'closed') return 3;
    return 4;
  };

  const handleChangePage = (pageNumber, pageSize) => {
    setPagination((prev) => ({ ...prev, pageNumber, pageSize }));

    const startIndex = (pageNumber - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    setFilteredList(
      applySort(kioskList, sortConfig).slice(startIndex, endIndex),
    );
  };

  const onFormChange = async (value, key, kiosk) => {
    try {
      const body = {
        ...kiosk,
        [key]: value,
      };
      await terminalApi.upsert(body);
    } catch (e) {
    } finally {
      getKioksList();
    }
  };

  useEffect(() => {
    getKioksList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClickSearch = async () => {
    await getKioksList();
    setPagination((prev) => ({ ...prev, pageNumber: 1 }));
  };

  const onChangeFilterInput = (value, key) => {
    setSearch((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const onClickClearSearch = () => {
    setSearch({
      name: '',
      haserrors: '',
      isconnected: '',
      openclosestatus: '',
    });
  };

  const toggleRow = (kioskId) => {
    setOpenKioskId(openKioskId === kioskId ? null : kioskId);
  };

  const transRef = useSpringRef();
  const transitions = useTransition(showFilters, {
    ref: transRef,
    keys: null,
    from: { maxHeight: '0px', opacity: 0 },
    enter: { maxHeight: '1000px', opacity: 1 },
    leave: { maxHeight: '0px', opacity: 0 },
  });

  useEffect(() => {
    transRef.start();
  }, [showFilters, transRef]);

  const hasKiosks = filteredList.length > 0;

  const renderConnectionBadge = (kiosk) => {
    if (kiosk.isconnected) {
      return (
        <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-400/20">
          <WifiIcon className="h-3 w-3" />
          Connected
        </span>
      );
    }
    const isClosed = getAvailability(kiosk.store)?.status === 'closed';
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1',
          isClosed
            ? 'bg-slate-100 text-slate-600 ring-slate-200 dark:bg-slate-700/50 dark:text-slate-300 dark:ring-slate-600/40'
            : 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-400/20',
        )}
      >
        <ExclamationCircleIcon className="h-3 w-3" />
        Offline
      </span>
    );
  };

  const renderCheckBadge = (kiosk, timestampKey, tooltipId) => {
    const timestamp = kiosk[timestampKey];
    if (!timestamp) {
      return (
        <span className="text-xs text-slate-300 dark:text-slate-600">—</span>
      );
    }
    const isFresh = checkConnection(timestamp);
    const isClosed = getAvailability(kiosk.store)?.status === 'closed';
    const tooltipContent = dayjs(timestamp).format('MM/DD/YYYY hh:mm A');

    if (isFresh) {
      return (
        <>
          <Tooltip id={tooltipId} />
          <span
            data-tooltip-id={tooltipId}
            data-tooltip-content={tooltipContent}
            className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-400/20"
          >
            <CheckCircleIcon className="h-3 w-3" />
            Checked
          </span>
        </>
      );
    }
    return (
      <>
        <Tooltip id={tooltipId} />
        <span
          data-tooltip-id={tooltipId}
          data-tooltip-content={tooltipContent}
          className={cn(
            'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 tabular-nums',
            isClosed
              ? 'bg-slate-100 text-slate-600 ring-slate-200 dark:bg-slate-700/50 dark:text-slate-300 dark:ring-slate-600/40'
              : 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-400/20',
          )}
        >
          {dayjs(timestamp).format('hh:mm A')}
        </span>
      </>
    );
  };

  return (
    <div className="kiosks-shell min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-7xl space-y-8">
        <PageHeader
          title="Kiosks"
          description="Monitor kiosk availability, reader connections, and interaction activity."
        />

        <div className="reveal">
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={cn(
                'inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-medium transition',
                showFilters
                  ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-400'
                  : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-300 dark:hover:bg-slate-700',
              )}
            >
              <AdjustmentsHorizontalIcon className="h-4 w-4" />
              Filters
              {showFilters && <XMarkIcon className="h-4 w-4" />}
            </button>
            {pagination.totalRecords != null && !loading.listLoading && (
              <span className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
                <ComputerDesktopIcon className="h-4 w-4" />
                {Number(pagination.totalRecords).toLocaleString('en-US')} total
              </span>
            )}
          </div>

          {transitions(
            (style, item) =>
              item && (
                <animated.div style={{ ...style, overflow: 'hidden' }}>
                  <div className="mt-3 rounded-md border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800/70 sm:p-5">
                    <div className="grid grid-cols-6 gap-3">
                      <div className="col-span-6 sm:col-span-2">
                        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                          Search
                        </label>
                        <input
                          value={search.name}
                          onChange={(e) =>
                            onChangeFilterInput(e.target.value, 'name')
                          }
                          type="text"
                          placeholder="Search by name…"
                          className="block w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 transition focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder-slate-500 dark:focus:border-sky-400 dark:focus:ring-sky-400"
                        />
                      </div>
                      <div className="col-span-6 sm:col-span-2">
                        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                          Hours
                        </label>
                        <Select
                          size="large"
                          style={{ width: '100%' }}
                          placeholder="Hours"
                          value={search.openclosestatus}
                          onChange={(e) => {
                            setSearch((prev) => ({
                              ...prev,
                              openclosestatus: e,
                            }));
                          }}
                        >
                          <Select.Option value="">All</Select.Option>
                          {Object.keys(openCloseStatus).map((key) => (
                            <Select.Option key={key} value={key}>
                              {openCloseStatus[key]}
                            </Select.Option>
                          ))}
                        </Select>
                      </div>
                      <div className="col-span-6 sm:col-span-2">
                        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                          Connections
                        </label>
                        <Select
                          size="large"
                          style={{ width: '100%' }}
                          placeholder="Connections"
                          value={search.haserrors}
                          onChange={(e) => {
                            setSearch((prev) => ({
                              ...prev,
                              haserrors: e,
                            }));
                          }}
                        >
                          <Select.Option value="">All</Select.Option>
                          <Select.Option key={1} value={false}>
                            Checked
                          </Select.Option>
                          <Select.Option key={2} value={true}>
                            Has Error
                          </Select.Option>
                        </Select>
                      </div>
                    </div>
                    <div className="mt-4 flex items-center justify-end gap-2">
                      <button
                        onClick={onClickClearSearch}
                        className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                      >
                        <XMarkIcon className="h-4 w-4" />
                        Clear
                      </button>
                      <button
                        onClick={onClickSearch}
                        className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 dark:bg-sky-500 dark:hover:bg-sky-400"
                      >
                        <MagnifyingGlassIcon className="h-4 w-4" />
                        Search
                      </button>
                    </div>
                  </div>
                </animated.div>
              ),
          )}
        </div>

        {loading.listLoading ? (
          <DataTableSkeleton
            rows={6}
            headers={[
              { label: 'Kiosk', w: 'w-40', shape: 'avatar' },
              {
                label: 'Interactions',
                align: 'center',
                w: 'w-12',
                shape: 'pill',
              },
              { label: 'Hours', align: 'center', w: 'w-20', shape: 'pill' },
              { label: 'Reader', align: 'center', w: 'w-20', shape: 'pill' },
              { label: 'Tablet', align: 'center', w: 'w-16', shape: 'pill' },
              {
                label: 'Reader Check',
                align: 'center',
                w: 'w-20',
                shape: 'pill',
              },
              { align: 'right', w: 'w-16' },
            ]}
          />
        ) : !hasKiosks ? (
          <div className="reveal flex flex-col items-center justify-center gap-3 rounded-md border border-dashed border-slate-300 bg-white px-6 py-16 text-center dark:border-slate-700 dark:bg-slate-800/50">
            <img src={NoData2} alt="" className="h-24 opacity-80" />
            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              No kiosks found
            </h3>
            <p className="max-w-xs text-sm text-slate-500 dark:text-slate-400">
              Try adjusting your filters or check back later.
            </p>
          </div>
        ) : (
          <div className="reveal">
            <TWDataTable
              columns={[
                {
                  title: (
                    <SortableHeader
                      label="Kiosk"
                      columnKey="store.company.name"
                      sortConfig={sortConfig}
                      onSort={requestSort}
                    />
                  ),
                  key: 'kiosk',
                  render: (_, kiosk) => (
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="shrink-0">
                        {kiosk.store?.company?.logo ? (
                          <img
                            alt="logo"
                            className="h-10 w-10 rounded-md object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                            src={kiosk.store?.company?.logo}
                          />
                        ) : (
                          <div
                            className={cn(
                              'flex h-10 w-10 items-center justify-center rounded-md bg-gradient-to-br text-xs font-bold text-white shadow-sm',
                              getAvatarColor(kiosk.store?.company?.name),
                            )}
                          >
                            {getInitials(kiosk.store?.company?.name)}
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <button
                          type="button"
                          onClick={() => toggleRow(kiosk.id)}
                          className="flex items-center gap-1 text-left"
                        >
                          <span className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                            {kiosk.name}
                          </span>
                          {openKioskId === kiosk.id ? (
                            <ChevronUpIcon className="h-4 w-4 shrink-0 text-slate-400" />
                          ) : (
                            <ChevronDownIcon className="h-4 w-4 shrink-0 text-slate-400" />
                          )}
                        </button>
                        <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                          {kiosk.store?.company?.name}
                        </p>
                      </div>
                    </div>
                  ),
                },
                {
                  title: (
                    <SortableHeader
                      label="Interactions"
                      columnKey="clickcount"
                      sortConfig={sortConfig}
                      onSort={requestSort}
                      align="center"
                    />
                  ),
                  key: 'clickcount',
                  align: 'center',
                  render: (_, kiosk) => (
                    <span className="inline-flex items-center rounded-md bg-slate-100 px-2.5 py-0.5 text-xs font-bold tabular-nums text-slate-700 dark:bg-slate-700/60 dark:text-slate-200">
                      {kiosk.clickcount}
                    </span>
                  ),
                },
                {
                  title: <span className={headerCls}>Hours</span>,
                  key: 'hours',
                  align: 'center',
                  render: (_, kiosk) => {
                    const availability = getAvailability(kiosk.store);
                    return availabilityBadge(
                      availability.status,
                      availability.label,
                    );
                  },
                },
                {
                  title: (
                    <SortableHeader
                      label="Reader"
                      columnKey="isconnected"
                      sortConfig={sortConfig}
                      onSort={requestSort}
                      align="center"
                    />
                  ),
                  key: 'isconnected',
                  align: 'center',
                  render: (_, kiosk) => renderConnectionBadge(kiosk),
                },
                {
                  title: (
                    <SortableHeader
                      label="Tablet"
                      columnKey="lastup"
                      sortConfig={sortConfig}
                      onSort={requestSort}
                      align="center"
                    />
                  ),
                  key: 'lastup',
                  align: 'center',
                  render: (_, kiosk) =>
                    renderCheckBadge(kiosk, 'lastup', `lastup-${kiosk.id}`),
                },
                {
                  title: (
                    <SortableHeader
                      label="Reader Check"
                      columnKey="lastconnectionupdate"
                      sortConfig={sortConfig}
                      onSort={requestSort}
                      align="center"
                    />
                  ),
                  key: 'lastconnectionupdate',
                  align: 'center',
                  render: (_, kiosk) =>
                    renderCheckBadge(
                      kiosk,
                      'lastconnectionupdate',
                      `connupdate-${kiosk.id}`,
                    ),
                },
                {
                  title: <span className="sr-only">Actions</span>,
                  key: 'actions',
                  align: 'right',
                  render: (_, kiosk) => (
                    <div className="flex items-center justify-end gap-3">
                      {openDevicePath(kiosk, roles) && (
                        <Link
                          to={openDevicePath(kiosk, roles)}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-blue-600 transition hover:bg-blue-50 dark:text-sky-400 dark:hover:bg-sky-500/10"
                          title="Open device"
                          aria-label="Open device"
                        >
                          <DeviceTabletIcon className="h-4 w-4" />
                        </Link>
                      )}
                      {kiosk.link && (
                        <button
                          type="button"
                          onClick={() =>
                            onClickOpenReaderLinkNewTab(kiosk.link)
                          }
                          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-blue-600 transition hover:bg-blue-50 dark:text-sky-400 dark:hover:bg-sky-500/10"
                          title="Open reader link"
                        >
                          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                        </button>
                      )}
                      <Tooltip id={`slacknotify-${kiosk.id}`} />
                      <button
                        type="button"
                        data-tooltip-id={`slacknotify-${kiosk.id}`}
                        data-tooltip-content={
                          kiosk.stopslacknotification
                            ? 'Slack notifications muted'
                            : 'Stop slack notifications'
                        }
                        onClick={() =>
                          onFormChange(
                            !kiosk.stopslacknotification,
                            'stopslacknotification',
                            kiosk,
                          )
                        }
                        className={cn(
                          'inline-flex h-7 items-center gap-1 rounded-md border px-2 text-[0.7rem] font-semibold transition',
                          kiosk.stopslacknotification
                            ? 'border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 dark:border-rose-400/30 dark:bg-rose-500/10 dark:text-rose-300 dark:hover:bg-rose-500/20'
                            : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700',
                        )}
                      >
                        <BellSlashIcon className="h-3.5 w-3.5" />
                        {kiosk.stopslacknotification ? 'Muted' : 'Mute'}
                      </button>
                    </div>
                  ),
                },
              ]}
              data={filteredList}
              rowKey="id"
              expandable={{
                expandedRowKeys: openKioskId ? [openKioskId] : [],
                showExpandColumn: false,
                expandedRowRender: (kiosk) => (
                  <div className="space-y-1.5 rounded-md border border-slate-100 bg-slate-50/60 p-3 text-sm dark:border-slate-700/60 dark:bg-slate-900/40">
                    <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
                      <BuildingStorefrontIcon className="h-3.5 w-3.5 text-slate-400" />
                      <Link
                        to={`/dashboard/companies/${kiosk.store?.company?.id}`}
                        className="font-medium hover:text-blue-600 dark:hover:text-sky-400"
                      >
                        {kiosk.store?.company?.name}
                      </Link>
                    </div>
                    <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
                      <ComputerDesktopIcon className="h-3.5 w-3.5 text-slate-400" />
                      {kiosk.store?.name}
                    </div>
                    <button
                      type="button"
                      onClick={() => onClickEditKioskLink(kiosk)}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700 dark:text-sky-400 dark:hover:text-sky-300"
                    >
                      <BoltIcon className="h-3 w-3" />
                      Remote View
                    </button>
                  </div>
                ),
              }}
              pagination={
                pagination.totalRecords > pagination.pageSize
                  ? {
                      current: pagination.pageNumber,
                      pageSize: pagination.pageSize,
                      total: pagination.totalRecords,
                      showTotal: (total, range) =>
                        `Showing ${range[0]} to ${range[1]} of ${total} kiosks`,
                      onChange: (pageNumber, pageSize) =>
                        handleChangePage(pageNumber, pageSize),
                      showSizeChanger: false,
                    }
                  : undefined
              }
            />
          </div>
        )}
      </div>

      <KioskLinkUpsertModal
        show={dialogs.showKioskLinkUpsertModal}
        kiosk={selectedKiosk}
        onSubmit={onSubmitReaderLink}
        onClose={() =>
          setDialogs((prev) => ({
            ...prev,
            showKioskLinkUpsertModal: false,
          }))
        }
      />
    </div>
  );
};

export default Kiosks;
